import axios from "axios";

/*
 * Sign-in session.
 *
 * The API gives a 15-minute access token and keeps a refresh token in an
 * httpOnly cookie. Every request uses the newest access token; a 401 gets
 * one quiet refresh and a retry before the user is signed out.
 */

const API_BASE = "/api";
const STORAGE_KEY = "textingToken";

// Refresh a little before the 15-minute token runs out.
const KEEP_FRESH_MS = 12 * 60 * 1000;

let accessToken = readStoredToken();
let refreshInFlight = null;
let sessionEndedHandler = () => {};

function readStoredToken() {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY) || "";
  } catch {
    return "";
  }
}

export function getAccessToken() {
  return accessToken;
}

export function setAccessToken(token) {
  accessToken = token || "";

  try {
    if (accessToken) {
      window.sessionStorage.setItem(STORAGE_KEY, accessToken);
    } else {
      window.sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Storage blocked: the token still lives in memory.
  }
}

// Called when the session can no longer be refreshed.
export function onSessionEnded(handler) {
  sessionEndedHandler = handler;
}

function isAuthRequest(url = "") {
  return url.startsWith(`${API_BASE}/auth/`);
}

/*
 * Gets a new access token from the refresh cookie. Concurrent callers
 * share one request, since each refresh replaces the cookie.
 */
export function refreshSession() {
  if (!refreshInFlight) {
    refreshInFlight = axios
      .post(`${API_BASE}/auth/refresh`)
      .then((response) => {
        setAccessToken(response.data.token);
        return response.data;
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }

  return refreshInFlight;
}

export async function signOut() {
  try {
    await axios.post(`${API_BASE}/auth/logout`);
  } catch {
    // Signed out locally either way.
  }

  setAccessToken("");
}

axios.interceptors.request.use((config) => {
  const authorization = config.headers?.Authorization;

  if (
    accessToken &&
    typeof authorization === "string" &&
    authorization.startsWith("Bearer ")
  ) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  return config;
});

axios.interceptors.response.use(null, async (error) => {
  const original = error.config;

  if (
    error.response?.status !== 401 ||
    !original ||
    original._retried ||
    isAuthRequest(original.url) ||
    !accessToken
  ) {
    throw error;
  }

  original._retried = true;

  try {
    await refreshSession();
  } catch {
    setAccessToken("");
    sessionEndedHandler();
    throw error;
  }

  return axios(original);
});

window.setInterval(() => {
  if (accessToken) {
    refreshSession().catch(() => {});
  }
}, KEEP_FRESH_MS);
